import React, { useState, useEffect } from 'react';
import { Upload, X, CheckCircle, AlertCircle, FileText, File } from 'lucide-react';
import { API_BASE } from '../../constants';

function LabResultsUploadModal({ onClose, onSuccess }) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [manualBatchId, setManualBatchId] = useState('');
  const [availableBatches, setAvailableBatches] = useState([]);
  const [showBatchSelector, setShowBatchSelector] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      return ext === 'csv' || ext === 'pdf';
    });
    
    if (validFiles.length !== files.length) {
      alert('Only CSV and PDF files are allowed');
    }
    
    setSelectedFiles([...selectedFiles, ...validFiles]);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      return ext === 'csv' || ext === 'pdf';
    });
    
    if (validFiles.length !== files.length) {
      alert('Only CSV and PDF files are allowed');
    }
    
    setSelectedFiles([...selectedFiles, ...validFiles]);
  };

  const removeFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  // Fetch available batches without lab results
  useEffect(() => {
    const fetchBatches = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE}/batches`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          // Filter batches without lab results
          const batchesWithoutResults = data.filter(b => !b.lab_results_received);
          setAvailableBatches(batchesWithoutResults);
        }
      } catch (err) {
        console.error('Error fetching batches:', err);
      }
    };
    fetchBatches();
  }, []);

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      alert('Please select at least one file to upload');
      return;
    }

    // Check if filename has batch ID or if manual batch is selected
    const firstFile = selectedFiles[0];
    const hasFilenameFormat = /\d{5}_\d{2}-\d{5}_.*_PROFESSIONAL\.(csv|CSV)/.test(firstFile.name);
    
    if (!hasFilenameFormat && !manualBatchId && selectedFiles.length === 1 && firstFile.name.endsWith('.csv')) {
      setShowBatchSelector(true);
      return;
    }

    setUploading(true);
    setUploadResults([]);

    try {
      const token = localStorage.getItem('token');
      
      // Use new endpoint if manual batch ID is provided
      if (manualBatchId && selectedFiles.length === 1) {
        const formData = new FormData();
        formData.append('file', selectedFiles[0]);
        formData.append('batch_id', manualBatchId);

        const res = await fetch(`${API_BASE}/lab-results/upload-with-batch?batch_id=${encodeURIComponent(manualBatchId)}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (res.ok) {
          const data = await res.json();
          setUploadResults([{
            filename: data.filename,
            status: data.status,
            message: data.message,
            batch_id: data.batch_id,
            matched_count: data.matched_count,
            imported_count: data.imported_count,
            unmatched_count: data.unmatched_count
          }]);
          
          setTimeout(() => {
            onSuccess();
            onClose();
          }, 3000);
        } else {
          const error = await res.json();
          alert(`Upload failed: ${error.detail || 'Unknown error'}`);
        }
      } else {
        // Use old endpoint for files with batch ID in filename
        const formData = new FormData();
        selectedFiles.forEach(file => {
          formData.append('files', file);
        });

        const res = await fetch(`${API_BASE}/lab-results/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (res.ok) {
          const data = await res.json();
          setUploadResults(data.results);
          
          // Check if all uploads were successful
          const allSuccess = data.results.every(r => r.status === 'success');
          if (allSuccess) {
            setTimeout(() => {
              onSuccess();
              onClose();
            }, 2000);
          }
        } else {
          const error = await res.json();
          alert(`Upload failed: ${error.detail || 'Unknown error'}`);
        }
      }
    } catch (err) {
      alert(`Upload error: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (ext === 'pdf') {
      return <File size={20} className="text-red-500" />;
    }
    return <FileText size={20} className="text-green-500" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Upload Lab Results</h2>
            <p className="text-sm text-gray-500 mt-1">
              Upload CSV and PDF files from the laboratory
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">
          {/* File Drop Zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-lg font-medium text-gray-700 mb-2">
              Drop files here or click to browse
            </p>
            <p className="text-sm text-gray-500 mb-4">
              CSV and PDF files only
            </p>
            <label className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 transition">
              <span>Select Files</span>
              <input
                type="file"
                multiple
                accept=".csv,.pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>
          </div>

          {/* Batch Selection (if needed) */}
          {showBatchSelector && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm font-medium text-yellow-900 mb-3">
                ⚠️ Batch ID not found in filename. Please select the batch manually:
              </p>
              <select
                value={manualBatchId}
                onChange={(e) => setManualBatchId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">-- Select Batch --</option>
                {availableBatches.map(batch => (
                  <option key={batch.batch_id} value={batch.batch_id}>
                    {batch.batch_id} - {batch.sample_count} samples ({new Date(batch.submission_date).toLocaleDateString()})
                  </option>
                ))}
              </select>
              <p className="text-xs text-yellow-700 mt-2">
                The system will match lab results to samples using farm, field, and sample names.
              </p>
            </div>
          )}

          {/* Expected Filename Format Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm font-medium text-blue-900 mb-2">
              Recommended Filename Format:
            </p>
            <code className="text-xs text-blue-800 bg-white px-2 py-1 rounded">
              {'{'}ControlID{'}'}_{'{'}BatchID{'}'}_{'{'}GROWER{'}'}_PROFESSIONAL.csv
            </code>
            <p className="text-xs text-blue-700 mt-2">
              Example: 88490_25-00024_KINSEY_FAMILY_VINEYARD_PROFESSIONAL.csv
            </p>
            <p className="text-xs text-blue-600 mt-2">
              💡 If filename doesn't contain batch ID, you can select it manually above.
            </p>
          </div>

          {/* Selected Files List */}
          {selectedFiles.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-700">
                Selected Files ({selectedFiles.length})
              </h3>
              <div className="space-y-2">
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {getFileIcon(file.name)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFile(index)}
                      className="text-gray-400 hover:text-red-600 transition ml-2"
                      disabled={uploading}
                    >
                      <X size={20} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Results */}
          {uploadResults.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-700">Upload Results</h3>
              <div className="space-y-2">
                {uploadResults.map((result, index) => (
                  <div
                    key={index}
                    className={`flex items-start gap-3 p-3 rounded-lg border ${
                      result.status === 'success'
                        ? 'bg-green-50 border-green-200'
                        : 'bg-red-50 border-red-200'
                    }`}
                  >
                    {result.status === 'success' ? (
                      <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {result.filename}
                      </p>
                      {result.status === 'success' ? (
                        <div className="text-xs text-gray-600 mt-1">
                          <p>Batch: {result.batch_id}</p>
                          {result.sample_count && (
                            <p>{result.sample_count} samples imported</p>
                          )}
                          {result.matched_count !== undefined && (
                            <div className="mt-1">
                              <p>✓ Matched: {result.matched_count} samples</p>
                              <p>✓ Imported: {result.imported_count} results</p>
                              {result.unmatched_count > 0 && (
                                <p className="text-yellow-600">⚠ Unmatched: {result.unmatched_count} samples</p>
                              )}
                            </div>
                          )}
                          <p className="text-green-700 font-medium mt-1">
                            {result.message}
                          </p>
                        </div>
                      ) : (
                        <p className="text-xs text-red-600 mt-1">{result.message}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
            disabled={uploading}
          >
            {uploadResults.length > 0 ? 'Close' : 'Cancel'}
          </button>
          <button
            onClick={handleUpload}
            disabled={uploading || selectedFiles.length === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium transition"
          >
            {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default LabResultsUploadModal;
